/**
 * 爱微圈导航 · 编辑 / 文件夹 / 右键菜单
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, gateGuest, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export async function showEditModal(sc) {
    const isFolder = parseInt(sc.is_folder) === 1;
    const isCard = state.settings.display_mode === 'card';
    let catOptions = '';
    if (!isFolder && isCard) {
        try {
            const all = await apiGet(API.shortcuts + '?action=all_categories');
            const cats = all.categories || [];
            const cur = sc.category || '常用推荐';
            catOptions = cats.map(c => `<option value="${escAttr(c)}" ${c === cur ? 'selected' : ''}>${esc(c)}</option>`).join('');
        } catch(e) {
            catOptions = `<option value="${escAttr(sc.category || '常用推荐')}" selected>${esc(sc.category || '常用推荐')}</option>`;
        }
    }
    const categoryField = (!isFolder && isCard)
        ? `<div class="form-group"><label>分组</label><select class="form-input" id="scCategory">${catOptions}</select></div>`
        : '';

    // 编辑状态：用于「自动获取」时判断是否覆盖用户已手动修改的字段
    const editState = {
        editedName: false,
        info: null,
        iconMode: (sc.icon_url && !sc.icon_url.includes('google.com/s2/favicons')) ? 'auto' : 'text',
    };

    openModal(`
        <div class="modal-title">${isFolder ? '编辑文件夹' : '编辑快捷方式'}</div>
        <div class="modal-error" id="editErr"></div>
        ${isFolder ? '' : `
        <div style="display:flex;align-items:center;gap:14px;padding:14px;background:var(--glass);border-radius:14px;margin-bottom:16px">
            <div id="editIconPrev" style="width:52px;height:52px;border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#fff;background:${escAttr(sc.icon_color || '#4A90D9')};flex-shrink:0;overflow:hidden;box-shadow:0 4px 14px rgba(0,0,0,0.12)">${esc(sc.icon_text || sc.name.charAt(0))}</div>
            <div style="flex:1;min-width:0">
                <div id="editNamePrev" style="font-size:15px;font-weight:600;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(sc.name)}</div>
                <div id="editDescPrev" style="font-size:12px;color:var(--text-faint);margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(sc.description || '修改网址后可点击「自动获取」拉取站点信息')}</div>
            </div>
        </div>`}
        <div class="form-group"><label>名称</label><input type="text" class="form-input" id="scName" value="${escAttr(sc.name)}" maxlength="50"></div>
        ${isFolder ? '' : `
        <div class="form-group"><label>网址</label>
            <div style="display:flex;gap:8px">
                <input type="text" class="form-input" id="scUrl" value="${escAttr(sc.url)}" style="flex:1">
                <button class="btn btn-light" id="scFetch" type="button" style="white-space:nowrap">自动获取</button>
            </div>
        </div>
        <div class="form-group"><label>描述</label><input type="text" class="form-input" id="scDesc" value="${escAttr(sc.description || '')}" maxlength="200" placeholder="网站描述（详细模式下显示）"></div>
        ${categoryField}`}
        <div class="form-row2">
            <div class="form-group"><label>图标文字</label><input type="text" class="form-input" id="scText" value="${escAttr(sc.icon_text || '')}" maxlength="2"></div>
            <div class="form-group"><label>图标颜色</label><input type="color" class="form-input" id="scColor" value="${escAttr(sc.icon_color || '#4A90D9')}"></div>
        </div>
        ${isFolder ? '' : `
        <div class="form-group"><label>图标来源</label>
            <div class="icon-picker-grid" id="editIconMode" data-mini="1" style="grid-template-columns:repeat(2,1fr)">
                <div class="icon-picker-item ${editState.iconMode === 'auto' ? 'selected' : ''}" data-mode="auto" title="自动获取站点图标"><span class="letter">A</span></div>
                <div class="icon-picker-item ${editState.iconMode === 'text' ? 'selected' : ''}" data-mode="text" title="文字图标"><span class="letter">T</span></div>
            </div>
        </div>
        <input type="hidden" id="scIconUrl" value="${escAttr(sc.icon_url || '')}">`}
        <div class="modal-actions">
            <button class="btn btn-light" data-close-modal>取消</button>
            <button class="btn btn-primary" id="scSave">保存</button>
        </div>`);

    const updatePreview = () => {
        if (isFolder) return;
        const name = $('#scName').value.trim() || sc.name;
        const desc = $('#scDesc').value.trim();
        const text = ($('#scText').value.trim() || name.charAt(0)).toUpperCase();
        const color = $('#scColor').value || '#4A90D9';
        const iconUrl = $('#scIconUrl').value || '';
        const iconEl = $('#editIconPrev');
        if (editState.iconMode === 'auto' && iconUrl) {
            iconEl.innerHTML = `<img src="${escAttr(iconUrl)}" style="width:64%;height:64%;object-fit:contain">`;
            iconEl.style.background = '#fff';
        } else {
            iconEl.textContent = text;
            iconEl.style.background = color;
        }
        $('#editNamePrev').textContent = name;
        $('#editDescPrev').textContent = desc || (editState.info ? '已获取站点信息' : '修改网址后可点击「自动获取」拉取站点信息');
    };

    const fetchInfo = debounce(async () => {
        if (isFolder) return;
        const url = $('#scUrl').value.trim();
        if (url.length < 5 || !url.includes('.')) return;
        $('#editDescPrev').textContent = '正在获取站点信息…';
        try {
            const info = await apiGet(API.site_info + '?url=' + encodeURIComponent(url));
            editState.info = info;
            if (info && !info.error) {
                if (!editState.editedName && info.title) {
                    $('#scName').value = info.title.substring(0, 50);
                }
                if (info.description && !$('#scDesc').value.trim()) {
                    $('#scDesc').value = info.description.substring(0, 200);
                }
                if (info.icon_text) $('#scText').value = info.icon_text;
                if (info.icon_color) $('#scColor').value = info.icon_color;
                if (info.icon) {
                    $('#scIconUrl').value = info.icon;
                    editState.iconMode = 'auto';
                    $$('#editIconMode .icon-picker-item').forEach(x => x.classList.toggle('selected', x.dataset.mode === 'auto'));
                }
                updatePreview();
            }
        } catch(e) {
            $('#editDescPrev').textContent = '获取失败，可手动填写';
        }
    }, 500);

    if (!isFolder) {
        $('#scName').oninput = () => { editState.editedName = true; updatePreview(); };
        $('#scDesc').oninput = updatePreview;
        $('#scText').oninput = updatePreview;
        $('#scColor').oninput = updatePreview;
        $('#scUrl').oninput = () => { updatePreview(); fetchInfo(); };
        $('#scUrl').onblur = fetchInfo;
        $('#scFetch').onclick = () => { editState.editedName = false; fetchInfo(); };
        $$('#editIconMode .icon-picker-item').forEach(item => {
            item.onclick = () => {
                $$('#editIconMode .icon-picker-item').forEach(x => x.classList.remove('selected'));
                item.classList.add('selected');
                editState.iconMode = item.dataset.mode;
                updatePreview();
            };
        });
    }

    $('#scSave').onclick = async () => {
        try {
            const payload = {
                id: sc.id,
                name: $('#scName').value.trim(),
                icon_text: $('#scText').value.trim(),
                icon_color: $('#scColor').value,
            };
            if (!isFolder) {
                payload.url = $('#scUrl').value.trim();
                payload.description = $('#scDesc').value.trim();
                if (isCard) payload.category = $('#scCategory').value.trim() || '常用推荐';
                // 自动图标：提交 icon_url + icon_type='url'；文字图标：清空 icon_url 并退回到 color 类型
                if (editState.iconMode === 'auto' && $('#scIconUrl').value) {
                    payload.icon_url = $('#scIconUrl').value;
                    payload.icon_type = 'url';
                } else {
                    payload.icon_url = '';
                    payload.icon_type = 'color';
                }
            }
            await apiPost(API.shortcuts + '?action=update', payload);
            closeModal();
            await loadShortcuts();
            loadUserCategories();
            toast('已保存', 'success');
        } catch (e) { showModalError('#editErr', e.message); }
    };
    updatePreview();
}


export function showFolderModal(sc) {
    openModal(`
        <div class="modal-title" style="display:flex;align-items:center;gap:8px">
            <span>📁 ${esc(sc.name)}</span>
            <button class="btn btn-light btn-sm" id="folderEditBtn" style="margin-left:auto;font-size:12px;padding:4px 10px">编辑</button>
        </div>
        <div class="modal-error" id="folderErr"></div>
        <div class="folder-grid" id="folderGrid"><div class="folder-loading">加载中…</div></div>
        <div class="modal-actions">
            <button class="btn btn-light" data-close-modal>关闭</button>
        </div>`);
    const grid = $('#folderGrid');

    async function loadFolderContents() {
        try {
            const items = await apiGet(API.shortcuts + '?action=folder_contents&folder_id=' + sc.id);
            if (!items.length) {
                grid.innerHTML = '<div class="folder-empty">' + (state.site.empty_folder || '文件夹为空，拖拽快捷方式到此可添加') + '</div>';
                return;
            }
            grid.innerHTML = '';
            items.forEach((item, i) => {
                const el = document.createElement('div');
                el.className = 'folder-item';
                el.draggable = true;
                el.dataset.id = item.id;
                const iconBox = document.createElement('div');
                iconBox.className = 'folder-item-icon';
                iconBox.style.background = item.icon_color || '#4A90D9';
                if (item.icon_url) {
                    // 用 JS 属性绑定 onload/onerror（页面 CSP 的 script-src 不含 unsafe-inline，
                    // 内联 onload="..." 会被拦截，导致图标一直停在 opacity:0 不可见）
                    const img = document.createElement('img');
                    img.alt = '';
                    img.loading = 'lazy';
                    img.referrerPolicy = 'no-referrer';
                    img.onload = () => img.classList.add('loaded');
                    img.onerror = () => {
                        iconBox.textContent = (item.icon_text || (item.name || '?').charAt(0)).toUpperCase();
                    };
                    img.src = item.icon_url;
                    iconBox.appendChild(img);
                } else {
                    iconBox.textContent = item.icon_text || (item.name || '?').charAt(0);
                }
                const nameEl = document.createElement('div');
                nameEl.className = 'folder-item-name';
                nameEl.textContent = item.name || '';
                const removeBtn = document.createElement('button');
                removeBtn.className = 'folder-item-remove';
                removeBtn.title = '移出文件夹';
                removeBtn.textContent = '✕';
                el.append(iconBox, nameEl, removeBtn);
                removeBtn.onclick = async (ev) => {
                    ev.stopPropagation();
                    try {
                        await apiPost(API.shortcuts + '?action=remove_from_folder', { id: item.id, page: sc.page });
                        loadFolderContents();
                        loadShortcuts();
                        toast('已移出文件夹', 'success');
                    } catch (e) { toast(e.message, 'error'); }
                };
                el.onclick = () => {
                    const u = safeUrl(item.url);
                    if (u) {
                        if (state.settings.open_new_tab === '1') window.open(u, '_blank', 'noopener');
                        else location.href = u;
                    }
                };
                el.ondragstart = (e) => {
                    state.dragged = item;
                    state.dragSource = 'folder';
                    state._dragEl = el;
                    el.classList.add('dragging');
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', item.id);
                };
                el.ondragend = () => {
                    el.classList.remove('dragging');
                    $$('.drop-target', grid).forEach(x => x.classList.remove('drop-target'));
                    state.dragged = null;
                    state._dragEl = null;
                    state.dragSource = null;
                    setTimeout(() => { state._justDragged = false; }, 10);
                };
                el.ondragover = (e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (!state.dragged || state.dragged.id === item.id) return;
                    if (state.dragSource === 'folder') {
                        // 文件夹内部实时挤开：按光标在目标左/右半决定前插/后插
                        const rect = el.getBoundingClientRect();
                        const before = (e.clientX - rect.left) < rect.width * 0.5;
                        if (flipMove(grid, state._dragEl, el, before)) {
                            const ids = [...grid.querySelectorAll('.folder-item[data-id]')].map(n => parseInt(n.dataset.id, 10));
                            const byId = new Map(items.map(s => [s.id, s]));
                            const reordered = ids.map(id => byId.get(id)).filter(Boolean);
                            items.splice(0, items.length, ...reordered);
                            items.forEach((s, idx) => s.position = idx);
                        }
                        el.classList.remove('drop-target');
                    } else {
                        el.classList.add('drop-target');
                    }
                };
                el.ondragleave = (e) => {
                    if (!el.contains(e.relatedTarget)) el.classList.remove('drop-target');
                };
                el.ondrop = async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    el.classList.remove('drop-target');
                    if (!state.dragged || state.dragged.id === item.id) return;
                    // 从主网格拖到文件夹里 → 移入当前文件夹
                    if (state.dragSource === 'grid') {
                        try {
                            await apiPost(API.shortcuts + '?action=move_to_folder', { id: state.dragged.id, folder_id: sc.id });
                            state._justDragged = true;
                            loadFolderContents();
                            loadShortcuts();
                            toast('已移入文件夹', 'success');
                        } catch (err) { toast(err.message, 'error'); }
                        return;
                    }
                    // 内部排序：实时挤开已就位，此处仅持久化
                    state._justDragged = true;
                    try {
                        await apiPost(API.shortcuts + '?action=reorder', {
                            items: items.map(s => ({ id: s.id, position: s.position, page: sc.page }))
                        });
                    } catch {}
                    loadFolderContents();
                };
                grid.appendChild(el);
            });
        } catch (e) {
            grid.innerHTML = '<div class="folder-empty">加载失败</div>';
        }
    }

    $('#folderEditBtn').onclick = () => { closeModal(); setTimeout(() => showEditModal(sc), 80); };
    loadFolderContents();
}


export function showContextMenu(e, sc) {
    if (gateGuest()) return; // 编辑/删除属登录限定（2026-09-04 策略）
    const menu = $('#contextMenu');
    const isFolder = parseInt(sc.is_folder) === 1;
    menu.innerHTML = isFolder ? `
        <div class="context-item" data-act="open">📁 打开文件夹</div>
        <div class="context-item" data-act="edit">✎ 编辑</div>
        <div class="context-item danger" data-act="delete">🗑 删除</div>` : `
        <div class="context-item" data-act="edit">✎ 编辑</div>
        <div class="context-item" data-act="open">↗ 打开</div>
        <div class="context-item danger" data-act="delete">🗑 删除</div>`;
    menu.style.left = Math.min(e.clientX, window.innerWidth - 170) + 'px';
    menu.style.top = Math.min(e.clientY, window.innerHeight - 140) + 'px';
    menu.classList.add('show');
    menu.onclick = async (ev) => {
        const act = ev.target.dataset.act;
        menu.classList.remove('show');
        if (act === 'edit') showEditModal(sc);
        if (act === 'open') {
            if (isFolder) { showFolderModal(sc); return; }
            const u = safeUrl(sc.url); if (u) window.open(u, '_blank', 'noopener');
        }
        if (act === 'delete') {
            const ok = await modalConfirm(isFolder ? '删除文件夹' : '删除快捷方式',
                isFolder ? `确认删除文件夹「${sc.name}」？内部快捷方式将移回主界面。` : `确认删除「${sc.name}」？`);
            if (!ok) return;
            try { await apiPost(API.shortcuts + '?action=delete', { id: sc.id }); await loadShortcuts(); toast('已删除', 'success'); }
            catch (e) { toast(e.message, 'error'); }
        }
    };
}

/* ---------- 添加面板 ---------- */

